import RechargeCooldownLabel from "../../RechargeCooldownLabel";
import { RechargeCooldownError } from "@/store/rechargeCooldown";
import React, { useEffect, useRef, useState } from "react";
import "../flatRecharge.less";
import { useTranslationPlus } from "@/Core/i18n/useTranslationPlus";
import {
  useChangeFlatRecharge,
  useWalletAll,
  useWalletRechargeFlat,
} from "@/store/walletStore";
import { useUserInfo } from "@/store/userInfo";
import FormUI from '@/componentsUI/Form';
import FromItemPlus from "@/components/FormItemPlus/FromItemPlus";
import LoadingUI from "@/componentsUI/LoadingUI/LoadingUI";
import {
  IFWalletRechargeFlatReq,
} from "@/api/apiReq/IFWalletReq";
import FlatPix from "../pix/FlatPix";
import ButtonUI from "@/componentsUI/Button";
import { AmountMultiplierConversion } from "@/Core/utils/currencyDealCenter";
import useDialogManager from "@/utils/useDialogManager";
import DepositCurrency from "../compoments/depositCurrency/DepositCurrency";
import DepositWay from "../compoments/depositWay/DepositWay";
import MexicoConfirmModal from "@/pages/wallet/shared/MexicoConfirmModal/MexicoConfirmModal";
import { useRechargeCurrency } from "../../useRechargeCurrency";
import { useRechargePaymentMethod } from "../useRechargePaymentMethod";
import { getUserMobile } from "@/utils/mobileUtils";
import RechargeTutorial from "../compoments/rechargeTutorial/RechargeTutorial";
import { feedback } from "@/utils/feedback";

const FlatRechargeMexico = () => {
  const { t } = useTranslationPlus();
  const { data: wallets } = useWalletAll();
  const { data: userInfo } = useUserInfo();
  const [form] = FormUI.useForm();
  const { params, displayCurrencyType, changeRecharge } = useRechargeCurrency(wallets?.flat, form);
  const { isValidating, data, error, mutate } = useChangeFlatRecharge(params);
  const { trigger, isMutating, isRechargeBlocked } = useWalletRechargeFlat();
  const { openDialogIFrame } = useDialogManager();
  const formValidator = useRef(null);
  const { rule, changeType } = useRechargePaymentMethod(data?.payParams, form, params?.currencyType);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [pendingFormValues, setPendingFormValues] = useState(null);
  const [showTutorial, setShowTutorial] = useState(false);

  useEffect(() => {
    setShowPaymentModal(false);
    setPendingFormValues(null);
    formValidator.current = false;
  }, [rule, params?.currencyType]);

  function recharge() {
    if (!rule || error || isValidating || isMutating || isRechargeBlocked) return;
    formValidator.current = true;
    form
      .validate()
      .then((v) => {
        setPendingFormValues(v);
        setShowPaymentModal(true);
        formValidator.current = false;
      })
      .catch(() => {
        formValidator.current = false;
      });
  }

  function submitPayment(values) {
    if (!values || !rule || error || isValidating || isMutating || isRechargeBlocked) return;

    const selectedWallet = wallets?.flat?.find(
      (w) => w.currencyType === values?.walletType,
    );

    const paymentData: IFWalletRechargeFlatReq = {
      currencyType: selectedWallet?.serverCurrencyType || values?.walletType,
      money: AmountMultiplierConversion(values?.amount, {
        currencyType: selectedWallet?.currencyType,
        serverCurrencyType: selectedWallet?.serverCurrencyType,
      }),
      payCode: values?.payType?.data?.payCode,
      mobile: form.getFieldValue("mobile") || "",
      rechargeMobileUserId: userInfo?.userId,
    };

    trigger(paymentData)
      .then((res) => {
        setShowPaymentModal(false);
        setPendingFormValues(null);

        if (res?.payUrl) {
          openDialogIFrame({
            title: t("Wallet.Wallet_PayUrlTitle"),
            url: res?.payUrl,
            type: "pay",
          });
        } else {
          feedback.success(t("Wallet.Payment request submitted successfully"));
        }
      })
      .catch((e) => {
        if (e instanceof RechargeCooldownError) return;
        feedback.error(t(e?.code));
        setShowPaymentModal(false);
        setPendingFormValues(null);
      });
  }

  function handlePaymentConfirm() {
    if (!pendingFormValues) return;
    submitPayment(pendingFormValues);
  }

  function handlePaymentModalClose() {
    setShowPaymentModal(false);
    setPendingFormValues(null);
  }

  function handleOpenTutorial() {
    setShowTutorial(true);
  }

  function handleCloseTutorial() {
    setShowTutorial(false);
  }

  useEffect(() => {
    const mobile = getUserMobile(userInfo?.mobile, userInfo?.userId);
    if (mobile) {
      form.setFieldValue("mobile", mobile);
    }
  }, [userInfo]);

  useEffect(() => {
    if (error) {
      console.error("Flat recharge error:", {
        error,
        errorCode: error?.code,
        errorMessage: error?.message,
        params,
      });
    }
  }, [error, params]);

  return (
    <div className="flat-Recharge">
      <FormUI form={form} layout={"vertical"} autoComplete={"off"}>
        <LoadingUI
          isLoading={isValidating}
          error={error}
          dataList={data}
          retryFunc={mutate}
        >
          <div className="deposit-row">
            <FromItemPlus
              field={"walletType"}
              label={t("Wallet.Currency")}
              className="deposit-currency-item"
            >
              <DepositCurrency
                currencys={wallets?.flat}
                onChange={(_value: any, option: any) => {
                  changeRecharge(option?._index);
                }}
              />
            </FromItemPlus>
          </div>
          <div className="deposit-method-section">
            <FromItemPlus
              field={"payType"}
              label={t("Wallet.Method")}
              className="deposit-way-item"
              shouldUpdate
            >
              {(values) => (
                <DepositWay
                  payParams={data?.payParams}
                  onChange={changeType}
                  value={values?.payType}
                />
              )}
            </FromItemPlus>
          </div>
          <FlatPix
            data={data}
            form={form}
            currencyType={displayCurrencyType}
            serverCurrencyType={params?.currencyType}
            formValidator={formValidator}
            rule={rule}
          />
        </LoadingUI>
      </FormUI>
      <RechargeTutorial
        title={t("Wallet.DepositTutorialTitle")}
        hint={t("Wallet.DepositTutorialHint")}
        videoUrl="https://vedio.winzago.com/winzago/vedio/2026-0203-vedio.mp4"
        visible={showTutorial}
        onOpen={handleOpenTutorial}
        onClose={handleCloseTutorial}
      />
      <ButtonUI
        className={`recharge-btn`}
        data-recharge-spin-entry-anchor="deposit-submit"
        type="primary"
        loading={isMutating}
        disabled={!rule || !!error || isValidating || isMutating || isRechargeBlocked}
        onClick={recharge}
      >
        <RechargeCooldownLabel>{t("Wallet.Deposit")}</RechargeCooldownLabel>
      </ButtonUI>

      <MexicoConfirmModal
        disabled={isValidating || !!error || !rule || isRechargeBlocked}
        visible={showPaymentModal}
        onClose={handlePaymentModalClose}
        onConfirm={handlePaymentConfirm}
        type="deposit"
        loading={isMutating}
        autoTriggerSeconds={5}
      />
    </div>
  );
};

export default FlatRechargeMexico;
